// File: CategorySection.jsx
import React, { useRef, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogContent,
  Rating,
  useMediaQuery
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useSwipeable } from 'react-swipeable';

function getYoutubeId(url) {
  const match = url.match(/(?:v=|youtu\.be\/)([^&]+)/);
  return match ? match[1] : null;
}

function getThumbnail(url) {
  const id = getYoutubeId(url);
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : '';
}

export default function CategorySection({ title, videos }) {
  const scrollRef = useRef();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [hover, setHover] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [ratings, setRatings] = useState({});

  const scrollBy = (offset) => {
    scrollRef.current?.scrollBy({ left: offset, behavior: 'smooth' });
  };

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => scrollBy(300),
    onSwipedRight: () => scrollBy(-300),
    preventScrollOnSwipe: true,
    trackMouse: true
  });

  const handleRating = (id, newValue) => {
    setRatings(prev => ({ ...prev, [id]: newValue }));
  };

  const handleNext = () => {
    if (!selectedVideo) return;
    const currentIndex = videos.findIndex(v => v.id === selectedVideo.id);
    const nextVideo = videos[currentIndex + 1];
    if (nextVideo) {
      setSelectedVideo(nextVideo);
    } else {
      setSelectedVideo(null);
    }
  };

  return (
    <Box
      sx={{ position: 'relative', mb: 6 }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', ml: 1 }}>
        {title}
      </Typography>

      <Box
        ref={scrollRef}
        {...swipeHandlers}
        sx={{
          display: 'flex',
          overflowX: 'auto',
          gap: 2,
          pl: 1,
          pr: 1,
          scrollBehavior: 'smooth',
          '&::-webkit-scrollbar': { display: 'none' }
        }}
      >
        {videos.map(video => (
          <Box
            key={video.id}
            onClick={() => setSelectedVideo(video)}
            sx={{
              width: 220,
              flexShrink: 0,
              cursor: 'pointer',
              transition: 'transform 0.3s',
              '&:hover': { transform: 'scale(1.05)' }
            }}
          >
            <img
              src={getThumbnail(video.youtubeUrl)}
              alt={video.title}
              style={{
                width: '100%',
                height: 121,
                objectFit: 'cover',
                borderRadius: 4
              }}
            />
            <Typography variant="subtitle2" sx={{ mt: 0.5, color: '#222' }}>
              {video.title}
            </Typography>
            <Typography variant="caption" sx={{ color: '#666' }}>
              {video.description}
            </Typography>
            <Rating
              size="small"
              value={ratings[video.id] || 0}
              onChange={(e, newValue) => handleRating(video.id, newValue)}
              sx={{ mt: 0.5 }}
            />
          </Box>
        ))}
      </Box>

      {!isMobile && hover && (
        <>
          <IconButton
            onClick={() => scrollBy(-300)}
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              zIndex: 1,
              background: 'linear-gradient(to right, rgba(255,255,255,0.9), transparent)',
              border: 0,
              '&:hover': {
                background: 'linear-gradient(to right, rgba(255,255,255,0.96), rgba(255,255,255,0.4))',
                '& .MuiSvgIcon-root': {
                  color: '#000' // mörkare ikon på hover
                }
              }
            }}
          >
            <ChevronLeft sx={{ color: '#333', transition: 'color 0.3s' }} />
          </IconButton>

          <IconButton
            onClick={() => scrollBy(300)}
            sx={{
              position: 'absolute',
              right: 0,
              top: 0,
              bottom: 0,
              zIndex: 1,
              background: 'linear-gradient(to left, rgba(255,255,255,0.9), transparent)',
              borderLeft: 0,
              '&:hover': {
                background: 'linear-gradient(to left, rgba(255,255,255,0.96), rgba(255,255,255,0.4))',
                '& .MuiSvgIcon-root': {
                  color: '#000' // mörkare ikon på hover
                }
              }
            }}
          >
            <ChevronRight sx={{ color: '#333', transition: 'color 0.3s' }} />
          </IconButton>
        </>
      )}

      <Dialog
        open={!!selectedVideo}
        onClose={() => setSelectedVideo(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ p: 0 }}>
          {selectedVideo && (
            <Box>
              <iframe
                width="100%"
                height={isMobile ? 220 : 480}
                src={`https://www.youtube.com/embed/${getYoutubeId(selectedVideo.youtubeUrl)}?autoplay=1`}
                title={selectedVideo.title}
                frameBorder="0"
                allow="autoplay; encrypted-media"
                allowFullScreen
              />
              {videos.findIndex(v => v.id === selectedVideo.id) < videos.length - 1 && (
                <Box sx={{ p: 2 }}>
                  <Typography
                    variant="body2"
                    onClick={handleNext}
                    sx={{ color: '#1976d2', cursor: 'pointer' }}
                  >
                    Nästa video →
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
